/**
 * CREATION DETAIL MODAL
 * 
 * View and edit a creation with full inline editing
 * - Photo carousel
 * - Editable title, description, date, techniques, difficulty, time, price
 * - Items used (mini cards)
 * - Marketplace toggle and settings
 * - Edit/Save/Delete options
 */

import React, { useState, useEffect } from 'react';
import { X, Edit, Trash2, Store, Clock, Palette, DollarSign, ExternalLink, ChevronDown, ChevronRight, Tag, Calendar, Zap, Save, Loader2, Plus, Search, Eye, EyeOff } from 'lucide-react';
import { doc, updateDoc, serverTimestamp, collection, getDocs, addDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import ImageCarousel from '../ImageCarousel';
import type { Creation } from '../../types/creationTypes';
import { TECHNIQUES, CREATION_TYPES } from '../../types/creationTypes';
import { itemsUsedRefs as computeItemsUsedRefs } from '../../utils/creationRefs';
import { slugify } from '../../utils/polishSlug';
import { AppIcons } from '../../constants/icons';

// Item used type
interface ItemUsed {
  itemId: string;
  itemType?: string;
  name: string;
  brand: string;
  photoUrl?: string;
}

// Collection item type (for picker)
interface CollectionItem {
  id: string;
  name: string;
  brand: string;
  itemType?: string;
  photos?: { url: string; isDefault?: boolean }[];
  swatchPhotos?: string[];
  bottlePhotos?: string[];
}

interface CreationDetailModalProps {
  creation: Creation;
  onClose: () => void;
  onUpdate?: () => void;
  onDelete?: (id: string) => void;
  isPublicView?: boolean;
}

// Difficulty options
const DIFFICULTY_OPTIONS = ['beginner', 'intermediate', 'advanced', 'expert'];

// Time options
const TIME_OPTIONS = [
  '< 30 minutes',
  '30-60 minutes', 
  '1-2 hours',
  '2-3 hours',
  '3+ hours',
  'Multiple sessions'
];

const CreationDetailModal: React.FC<CreationDetailModalProps> = ({ 
  creation, 
  onClose, 
  onUpdate,
  onDelete,
  isPublicView = false
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['items', 'marketplace']));
  
  // Local state for ALL editable fields
  const [localTitle, setLocalTitle] = useState(creation.title || '');
  const [localDescription, setLocalDescription] = useState(creation.description || '');
  const [localDateCreated, setLocalDateCreated] = useState(creation.dateCreated || '');
  const [localTechniques, setLocalTechniques] = useState<string[]>(creation.techniques || []);
  const [localDifficulty, setLocalDifficulty] = useState(creation.difficulty || '');
  const [localTimeToComplete, setLocalTimeToComplete] = useState(creation.timeToComplete || '');
  const [localQuantity, setLocalQuantity] = useState(creation.quantity || 1);
  const [localPrice, setLocalPrice] = useState(creation.price || 0);
  const [localContactForPricing, setLocalContactForPricing] = useState(creation.contactForPricing || false);
  const [localListedOnMarketplace, setLocalListedOnMarketplace] = useState(creation.listedOnMarketplace || false);
  const [localMarketplaceStatus, setLocalMarketplaceStatus] = useState<'available' | 'pending' | 'sold'>(creation.marketplaceStatus || 'available');
  const [localMarketplaceCategory, setLocalMarketplaceCategory] = useState(creation.marketplaceCategory || '');
  const [localCreationType, setLocalCreationType] = useState(creation.creationType || 'mani');
  
  // Press-on details
  const [localShapesAvailable, setLocalShapesAvailable] = useState<string[]>(creation.pressOnDetails?.shapesAvailable || []);
  const [localLengthsAvailable, setLocalLengthsAvailable] = useState<string[]>(creation.pressOnDetails?.lengthsAvailable || []);
  const [localCustomSizing, setLocalCustomSizing] = useState(creation.pressOnDetails?.customSizing || false);
  const [localSizingKitAvailable, setLocalSizingKitAvailable] = useState(creation.pressOnDetails?.sizingKitAvailable || false);
  const [localGuideUrl, setLocalGuideUrl] = useState(creation.pressOnDetails?.guideUrl || '');
  
  // Technique picker state
  const [showTechniquePicker, setShowTechniquePicker] = useState(false);
  
  // Items used state
  const [localItemsUsed, setLocalItemsUsed] = useState<ItemUsed[]>(
    (creation.itemsUsed || []).map(item => ({
      itemId: item.itemId || '',
      itemType: item.itemType,
      name: item.name || '',
      brand: item.brand || '',
      photoUrl: item.photoUrl
    }))
  );
  
  // Item picker state
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [collectionItems, setCollectionItems] = useState<CollectionItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  
  // Public view - Watching state
  const [isWatching, setIsWatching] = useState(false);
  const [watchingLoading, setWatchingLoading] = useState(false);
  const [watchDocId, setWatchDocId] = useState<string | null>(null);
  
  // Public view - Selected item from Items Used
  const [selectedItemUsed, setSelectedItemUsed] = useState<ItemUsed | null>(null);
  const [addingToWishlist, setAddingToWishlist] = useState(false);
  const [wishlistSuccess, setWishlistSuccess] = useState(false);
  const [loginPrompt, setLoginPrompt] = useState(false);
  
  // Load collection items when picker opens
  useEffect(() => {
    const loadCollectionItems = async () => {
      const user = auth.currentUser;
      if (!user || !showItemPicker) return;
      
      setLoadingItems(true);
      try {
        const itemsRef = collection(db, 'users', user.uid, 'items');
        const snapshot = await getDocs(itemsRef);
        const items: CollectionItem[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.isDestashed) {
            items.push({ id: doc.id, ...data } as CollectionItem);
          }
        });
        // Sort by name
        items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCollectionItems(items);
      } catch (error) {
        console.error('Error loading collection:', error);
      } finally {
        setLoadingItems(false);
      }
    };
    
    loadCollectionItems();
  }, [showItemPicker]);
  
  // Check if user is watching this creation (public view only)
  useEffect(() => {
    const checkWatching = async () => {
      const user = auth.currentUser;
      if (!user || !isPublicView || !creation.id) return;
      
      try {
        const watchingRef = collection(db, 'users', user.uid, 'watchingCreations');
        const q = query(watchingRef, where('creationId', '==', creation.id));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          setIsWatching(true);
          setWatchDocId(snapshot.docs[0].id);
        } else {
          setIsWatching(false);
          setWatchDocId(null);
        }
      } catch (error) {
        console.error('Error checking watch status:', error);
      }
    };
    
    checkWatching();
  }, [isPublicView, creation.id]);
  
  // Handle watching/unwatching
  const handleToggleWatch = async () => {
    const user = auth.currentUser;
    if (!user) {
      setLoginPrompt(true);
      return;
    }
    
    setWatchingLoading(true);
    try {
      const watchingRef = collection(db, 'users', user.uid, 'watchingCreations');
      
      if (isWatching && watchDocId) {
        // Unwatch
        await deleteDoc(doc(db, 'users', user.uid, 'watchingCreations', watchDocId));
        setIsWatching(false);
        setWatchDocId(null);
      } else {
        // Watch
        const docRef = await addDoc(watchingRef, {
          creationId: creation.id,
          creatorId: creation.userId,
          title: creation.title,
          photoUrl: creation.photos?.[0]?.url || null,
          price: creation.price || null,
          marketplaceCategory: creation.marketplaceCategory || null,
          addedAt: serverTimestamp()
        });
        setIsWatching(true);
        setWatchDocId(docRef.id);
      }
    } catch (error) {
      console.error('Error toggling watch:', error);
    } finally {
      setWatchingLoading(false);
    }
  };
  
  // Handle adding item to wishlist
  const handleAddToWishlist = async (item: ItemUsed) => {
    const user = auth.currentUser;
    if (!user) {
      setLoginPrompt(true);
      return;
    }
    
    setAddingToWishlist(true);
    try {
      const itemsRef = collection(db, 'users', user.uid, 'items');
      
      // Build photos array WITH source tracking for DMCA
      const trackedPhotos = item.photoUrl ? [{
        url: item.photoUrl,
        isDefault: true,
        // DMCA Source Tracking
        uploadedBy: user.uid,
        uploadedAt: new Date().toISOString(),
        source: 'web' as const,
        sourceUrl: item.photoUrl,
        sourceUserId: creation.userId || undefined,
        dmcaStatus: 'active' as const,
      }] : [];

      // Preserve the item's real type so wishlist entries land in the
      // right category. `item` here is a Creation's ItemUsed entry which
      // now carries `itemType` for all 8 types. Fall back to polish only
      // if the entry is missing itemType (legacy creations pre-Phase-1).
      const resolvedItemType = (item as { itemType?: string }).itemType ?? 'polish';

      await addDoc(itemsRef, {
        name: item.name,
        nameLower: (item.name ?? '').toLowerCase(),
        nameSlug: slugify(item.name ?? ''),
        brand: item.brand,
        brandLower: (item.brand ?? '').toLowerCase(),
        brandSlug: slugify(item.brand ?? ''),
        photoUrl: item.photoUrl || null,
        photos: trackedPhotos,
        itemType: resolvedItemType,
        onWishlist: true,
        isoPriority: 'iso',
        isDestashed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        notes: `Spotted in a creation by another collector`
      });
      setWishlistSuccess(true);
      setTimeout(() => {
        setWishlistSuccess(false);
        setSelectedItemUsed(null);
      }, 1500);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    } finally {
      setAddingToWishlist(false);
    }
  };
  
  // Get item image helper
  const getCollectionItemImage = (item: CollectionItem): string | null => {
    if (item.photos?.length) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto?.url || item.photos[0].url;
    }
    if (item.swatchPhotos?.length) return item.swatchPhotos[0];
    if (item.bottlePhotos?.length) return item.bottlePhotos[0];
    return null;
  };
  
  // Filter collection items for picker
  const filteredCollectionItems = collectionItems.filter(item => {
    // Exclude already added items
    if (localItemsUsed.some(used => used.itemId === item.id)) return false;
    
    // Search filter
    if (itemSearchQuery.trim()) {
      const query = itemSearchQuery.toLowerCase();
      return (
        item.name?.toLowerCase().includes(query) ||
        item.brand?.toLowerCase().includes(query)
      );
    }
    return true;
  });
  
  // Add item to used list
  const addItemUsed = (item: CollectionItem) => {
    const photoUrl = getCollectionItemImage(item);
    const newItem: ItemUsed = {
      itemId: item.id,
      name: item.name || '',
      brand: item.brand || ''
    };
    if (item.itemType) newItem.itemType = item.itemType;
    if (photoUrl) newItem.photoUrl = photoUrl;
    setLocalItemsUsed(prev => [...prev, newItem]);
  };
  
  // Remove item from used list
  const removeItemUsed = (itemId: string) => {
    setLocalItemsUsed(prev => prev.filter(item => item.itemId !== itemId));
  };
  
  // Track if there are unsaved changes
  const hasChanges = 
    localTitle !== (creation.title || '') ||
    localDescription !== (creation.description || '') ||
    localDateCreated !== (creation.dateCreated || '') ||
    localCreationType !== (creation.creationType || 'mani') ||
    JSON.stringify(localTechniques) !== JSON.stringify(creation.techniques || []) ||
    localDifficulty !== (creation.difficulty || '') ||
    localTimeToComplete !== (creation.timeToComplete || '') ||
    localQuantity !== (creation.quantity || 1) ||
    localPrice !== (creation.price || 0) ||
    localContactForPricing !== (creation.contactForPricing || false) ||
    localListedOnMarketplace !== (creation.listedOnMarketplace || false) ||
    localMarketplaceStatus !== (creation.marketplaceStatus || 'available') ||
    localMarketplaceCategory !== (creation.marketplaceCategory || '') ||
    JSON.stringify(localShapesAvailable) !== JSON.stringify(creation.pressOnDetails?.shapesAvailable || []) ||
    JSON.stringify(localLengthsAvailable) !== JSON.stringify(creation.pressOnDetails?.lengthsAvailable || []) ||
    localCustomSizing !== (creation.pressOnDetails?.customSizing || false) ||
    localSizingKitAvailable !== (creation.pressOnDetails?.sizingKitAvailable || false) ||
    localGuideUrl !== (creation.pressOnDetails?.guideUrl || '') ||
    JSON.stringify(localItemsUsed.map(i => i.itemId)) !== JSON.stringify((creation.itemsUsed || []).map(i => i.itemId || ''));

  // Get images for carousel
  const images = creation.photos?.map(p => p.url) || [];

  // Toggle section
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Toggle technique selection
  const toggleTechnique = (technique: string) => {
    setLocalTechniques(prev => 
      prev.includes(technique)
        ? prev.filter(t => t !== technique)
        : [...prev, technique]
    );
  };

  // Save all changes
  const handleSaveChanges = async () => {
    const user = auth.currentUser;

    if (!user) {
      return;
    }

    if (!localTitle.trim()) {
      alert('Title is required');
      return;
    }

    setIsSaving(true);
    try {
      const creationRef = doc(db, 'users', user.uid, 'creations', creation.id);

      // Clean itemsUsed to remove any undefined photoUrl values
      const cleanedItemsUsed = localItemsUsed.map(item => {
        const cleaned: Record<string, any> = {
          itemId: item.itemId,
          name: item.name,
          brand: item.brand || ''
        };
        // Only add optional fields if they exist
        if (item.itemType) cleaned.itemType = item.itemType;
        if (item.photoUrl) cleaned.photoUrl = item.photoUrl;
        return cleaned;
      });

      // Build updates object, only including defined values
      const updates: Record<string, any> = {
        title: localTitle.trim(),
        description: localDescription.trim(),
        creationType: localCreationType,
        quantity: localQuantity,
        itemsUsed: cleanedItemsUsed,
        // Recompute the denormalized ref array so an edit that added
        // or removed items keeps the "Manis featuring this" section
        // accurate. See utils/creationRefs.ts for format.
        itemsUsedRefs: computeItemsUsedRefs(cleanedItemsUsed),
        listedOnMarketplace: localListedOnMarketplace,
        updatedAt: serverTimestamp()
      };
      
      // Only add optional fields if they have values (Firestore doesn't accept undefined)
      if (localDateCreated) updates.dateCreated = localDateCreated;
      if (localTechniques.length > 0) updates.techniques = localTechniques;
      if (localDifficulty) updates.difficulty = localDifficulty;
      if (localTimeToComplete) updates.timeToComplete = localTimeToComplete;
      if (localPrice) updates.price = localPrice;
      if (localContactForPricing) updates.contactForPricing = localContactForPricing;
      
      // Only set marketplace fields if listed
      if (localListedOnMarketplace) {
        updates.marketplaceStatus = localMarketplaceStatus;
        if (localMarketplaceCategory) updates.marketplaceCategory = localMarketplaceCategory;
        
        // Press-on details
        if (localMarketplaceCategory === 'pressons') {
          const pressOnDetails: Record<string, any> = {};
          if (localShapesAvailable.length > 0) pressOnDetails.shapesAvailable = localShapesAvailable;
          if (localLengthsAvailable.length > 0) pressOnDetails.lengthsAvailable = localLengthsAvailable;
          if (localCustomSizing) pressOnDetails.customSizing = localCustomSizing;
          if (localSizingKitAvailable) pressOnDetails.sizingKitAvailable = localSizingKitAvailable;
          if (localGuideUrl.trim()) pressOnDetails.guideUrl = localGuideUrl.trim();
          
          if (Object.keys(pressOnDetails).length > 0) {
            updates.pressOnDetails = pressOnDetails;
          }
        }
      }

      await updateDoc(creationRef, updates);

      // Notify parent and close modal
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setLocalTitle(creation.title || '');
    setLocalDescription(creation.description || '');
    setLocalDateCreated(creation.dateCreated || '');
    setLocalTechniques(creation.techniques || []);
    setLocalDifficulty(creation.difficulty || '');
    setLocalTimeToComplete(creation.timeToComplete || '');
    setLocalQuantity(creation.quantity || 1);
    setLocalPrice(creation.price || 0);
    setLocalContactForPricing(creation.contactForPricing || false);
    setLocalListedOnMarketplace(creation.listedOnMarketplace || false);
    setLocalMarketplaceStatus(creation.marketplaceStatus || 'available');
    setLocalMarketplaceCategory(creation.marketplaceCategory || '');
    setLocalCreationType(creation.creationType || 'mani');
    setLocalShapesAvailable(creation.pressOnDetails?.shapesAvailable || []);
    setLocalLengthsAvailable(creation.pressOnDetails?.lengthsAvailable || []);
    setLocalCustomSizing(creation.pressOnDetails?.customSizing || false);
    setLocalSizingKitAvailable(creation.pressOnDetails?.sizingKitAvailable || false);
    setLocalGuideUrl(creation.pressOnDetails?.guideUrl || '');
    setLocalItemsUsed(
      (creation.itemsUsed || []).map(item => ({
        itemId: item.itemId || '',
        itemType: item.itemType,
        name: item.name || '',
        brand: item.brand || '',
        photoUrl: item.photoUrl
      }))
    );
    setShowItemPicker(false);
    setItemSearchQuery('');
    setIsEditMode(false);
  };

  // Discard changes and close
  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Discard them?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Get type display (based on creationType)
  const getTypeDisplay = () => {
    switch (localCreationType) {
      case 'mani': return { label: 'Manicure', color: 'bg-pink-100 text-pink-700', icon: '💅' };
      case 'pedi': return { label: 'Pedicure', color: 'bg-rose-100 text-rose-700', icon: '🦶' };
      case 'nail-art': return { label: 'Nail Art', color: 'bg-violet-100 text-violet-700', icon: '🎨' };
      case 'practice': return { label: 'Practice', color: 'bg-slate-100 text-slate-700', icon: '📝' };
      case 'press-ons': return { label: 'Press-Ons', color: 'bg-purple-100 text-purple-700', icon: '✨' };
      case 'jewelry': return { label: 'Jewelry', color: 'bg-amber-100 text-amber-700', icon: '💍' };
      case 'other': return { label: 'Other', color: 'bg-teal-100 text-teal-700', icon: '📦' };
      default: return { label: 'Manicure', color: 'bg-pink-100 text-pink-700', icon: '💅' };
    }
  };

  const category = getTypeDisplay();

  // Shape and length options for press-ons
  const SHAPE_OPTIONS = ['Almond', 'Coffin', 'Oval', 'Round', 'Square', 'Squoval', 'Stiletto', 'Ballerina'];
  const LENGTH_OPTIONS = ['XS', 'Short', 'Medium', 'Long', 'XL'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 md:p-4 z-50">
      <div className="modal-light w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="modal-header-light">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isEditMode && !isPublicView ? (
              <select
                value={localCreationType}
                onChange={(e) => setLocalCreationType(e.target.value as any)}
                className={`px-2 py-1 rounded-full text-xs font-bold flex-shrink-0 border-2 border-gray-300 cursor-pointer ${category.color}`}
              >
                {CREATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`px-2 py-1 rounded-full text-xs font-bold flex-shrink-0 ${category.color}`}>
                {category.icon} {category.label}
              </span>
            )}
            {isEditMode && !isPublicView ? (
              <input
                type="text"
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                className="text-xl md:text-2xl font-bold text-light-primary border-2 border-gray-300 rounded-lg px-3 py-1 flex-1 min-w-0 font-heading"
                placeholder="Creation title..."
              />
            ) : (
              <h2 className="text-xl md:text-2xl font-bold text-light-primary truncate font-heading">
                {localTitle}
              </h2>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isPublicView ? (
              /* Public View - Show Watch button */
              <>
                <button
                  onClick={handleToggleWatch}
                  disabled={watchingLoading}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-default text-sm ${
                    isWatching 
                      ? 'bg-sky-100 text-sky-700 border border-sky-300 hover:bg-sky-200' 
                      : 'bg-sky-500 text-white hover:bg-sky-600'
                  }`}
                  title={isWatching ? 'Stop watching' : 'Watch this creation'}
                >
                  {isWatching ? (
                    <EyeOff className={`w-4 h-4 ${watchingLoading ? 'animate-pulse' : ''}`} />
                  ) : (
                    <Eye className={`w-4 h-4 ${watchingLoading ? 'animate-pulse' : ''}`} />
                  )}
                  {watchingLoading ? 'Saving...' : isWatching ? 'Watching' : 'Watch'}
                </button>
                <button onClick={handleClose} className="p-2 hover:bg-gray-50 rounded-full transition-default">
                  <X className="icon-md md:w-6 md:h-6" />
                </button>
              </>
            ) : (
              /* Owner View - Show Edit/Delete buttons */
              <>
                {!isEditMode ? (
                  <button
                    onClick={() => setIsEditMode(true)}
                    className="p-2 text-gray-900 hover:bg-gray-100 rounded-lg transition-default"
                    title="Edit Creation"
                  >
                    <Edit className="icon-md" />
                  </button>
                ) : (
                  <button
                    onClick={handleDiscardChanges}
                    className="px-3 py-1.5 text-light-secondary hover:bg-gray-50 rounded-lg transition-default text-sm"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-default"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button onClick={handleClose} className="p-2 hover:bg-gray-50 rounded-full transition-default">
                  <X className="icon-md md:w-6 md:h-6" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LEFT: Photos */}
            <div className="space-y-4">
              {images.length > 0 ? (
                <ImageCarousel images={images} alt={localTitle} />
              ) : (
                <div className="aspect-square bg-gradient-to-br from-fuchsia-200 to-purple-200 rounded-lg flex items-center justify-center">
                  <span className="text-8xl">💅</span>
                </div>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-lg p-3 text-center">
                  <Palette className="icon-md mx-auto text-light-primary mb-1" />
                  <p className="text-lg font-bold text-light-primary">{creation.itemsUsed?.length || 0}</p>
                  <p className="text-xs text-light-muted">Items Used</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3 text-center">
                  <Tag className="icon-md mx-auto text-purple-500 mb-1" />
                  {isEditMode && !isPublicView ? (
                    <input
                      type="number"
                      value={localQuantity}
                      onChange={(e) => setLocalQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full text-center text-lg font-bold text-light-primary bg-white border border-gray-300 rounded"
                      min="1"
                    />
                  ) : (
                    <p className="text-lg font-bold text-light-primary">{localQuantity}</p>
                  )}
                  <p className="text-xs text-light-muted">Quantity</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <DollarSign className="icon-md mx-auto text-green-500 mb-1" />
                  {isEditMode && !isPublicView ? (
                    <input
                      type="number"
                      value={localPrice || ''}
                      onChange={(e) => setLocalPrice(parseFloat(e.target.value) || 0)}
                      className="w-full text-center text-lg font-bold text-green-600 bg-white border border-gray-300 rounded"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  ) : (
                    <p className="text-lg font-bold text-green-600">
                      {localPrice ? `$${localPrice.toFixed(2)}` : '-'}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Price</p>
                </div>
              </div>
            </div>

            {/* RIGHT: Details */}
            <div className="space-y-4">
              {/* Description */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-bold text-light-secondary mb-2 font-body">
                  Description
                </h3>
                {isEditMode && !isPublicView ? (
                  <textarea
                    value={localDescription}
                    onChange={(e) => setLocalDescription(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg min-h-[100px] resize-none"
                    placeholder="Describe your creation..."
                  />
                ) : (
                  <p className="text-light-secondary whitespace-pre-wrap">
                    {localDescription || <span className="italic text-light-muted">No description</span>}
                  </p>
                )}
              </div>

              {/* Editable Details Section */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                {/* Date Created */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-light-secondary mb-1">
                    <Calendar className="icon-sm text-green-500" />
                    Date Created
                  </label>
                  {isEditMode && !isPublicView ? (
                    <input
                      type="date"
                      value={localDateCreated}
                      onChange={(e) => setLocalDateCreated(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  ) : (
                    <p className="text-light-primary">{localDateCreated || <span className="italic text-light-muted">Not set</span>}</p>
                  )}
                </div>

                {/* Techniques */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-light-secondary mb-1">
                    <Zap className="icon-sm text-gray-900" />
                    Techniques
                  </label>
                  {isEditMode && !isPublicView ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1">
                        {localTechniques.map((tech, idx) => (
                          <span
                            key={idx}
                            onClick={() => toggleTechnique(tech)}
                            className="px-2 py-0.5 bg-gray-700 text-white rounded-full text-xs cursor-pointer hover:bg-gray-800"
                          >
                            {tech} ×
                          </span>
                        ))}
                        <button
                          onClick={() => setShowTechniquePicker(!showTechniquePicker)}
                          className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs hover:bg-gray-300 flex items-center gap-1"
                        >
                          <Plus className="icon-xs" /> Add
                        </button>
                      </div>
                      {showTechniquePicker && (
                        <div className="p-2 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto">
                          <div className="flex flex-wrap gap-1">
                            {TECHNIQUES.filter(t => !localTechniques.includes(t)).map((tech, idx) => (
                              <button
                                key={idx}
                                onClick={() => toggleTechnique(tech)}
                                className="px-2 py-0.5 border-2 border-gray-300 text-gray-600 rounded-full text-xs hover:bg-gray-100 hover:text-gray-900"
                              >
                                {tech}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {localTechniques.length > 0 ? (
                        localTechniques.map((tech, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-900 rounded-full text-xs">
                            {tech}
                          </span>
                        ))
                      ) : (
                        <span className="italic text-gray-500 text-sm">No techniques</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Difficulty */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-light-secondary mb-1">
                    <Zap className="icon-sm text-yellow-500" />
                    Difficulty
                  </label>
                  {isEditMode && !isPublicView ? (
                    <select
                      value={localDifficulty}
                      onChange={(e) => setLocalDifficulty(e.target.value)}
                      className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select difficulty...</option>
                      {DIFFICULTY_OPTIONS.map(opt => (
                        <option key={opt} value={opt} className="capitalize">{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-light-primary capitalize">{localDifficulty || <span className="italic text-light-muted">Not set</span>}</p>
                  )}
                </div>

                {/* Time to Complete */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-light-secondary mb-1">
                    <Clock className="icon-sm text-blue-500" />
                    Time to Complete
                  </label>
                  {isEditMode && !isPublicView ? (
                    <select
                      value={localTimeToComplete}
                      onChange={(e) => setLocalTimeToComplete(e.target.value)}
                      className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select time...</option>
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-light-primary">{localTimeToComplete || <span className="italic text-light-muted">Not set</span>}</p>
                  )}
                </div>
              </div>

              {/* Items Used Section */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('items')}
                  className="w-full px-4 py-3 border border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-default"
                >
                  <div className="flex items-center gap-2">
                    <Palette className="icon-md text-light-primary" />
                    <span className="font-bold text-light-secondary font-body">
                      Items Used
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 text-light-primary text-xs rounded-full">
                      {localItemsUsed.length}
                    </span>
                  </div>
                  {expandedSections.has('items') ? (
                    <ChevronDown className="icon-md text-gray-500" />
                  ) : (
                    <ChevronRight className="icon-md text-gray-500" />
                  )}
                </button>
                {expandedSections.has('items') && (
                  <div className="p-4 space-y-3">
                    {/* Hint for public view */}
                    {isPublicView && localItemsUsed.length > 0 && (
                      <p className="text-xs text-light-primary flex items-center gap-1">
                        <AppIcons.Favorite className="icon-xs text-current" aria-label="Favorite" />
                        Tap an item to add it to your wishlist
                      </p>
                    )}
                    {/* Current items */}
                    {localItemsUsed.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {localItemsUsed.map((item, idx) => (
                          <div 
                            key={idx} 
                            className={`flex items-center gap-2 p-2 border border-gray-200 rounded-lg group ${
                              isPublicView ? 'cursor-pointer hover:bg-gray-50 hover:border-gray-300 border-transparent transition-default' : ''
                            }`}
                            onClick={() => isPublicView && setSelectedItemUsed(item)}
                          >
                            {item.photoUrl ? (
                              <img
                                src={item.photoUrl}
                                alt={item.name}
                                className="w-10 h-10 rounded object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                                <span>💅</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-light-primary truncate">{item.name}</p>
                              <p className="text-xs text-light-muted truncate">{item.brand}</p>
                            </div>
                            {isEditMode && !isPublicView && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeItemUsed(item.itemId);
                                }}
                                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                              >
                                <X className="icon-sm" />
                              </button>
                            )}
                            {isPublicView && (
                              <AppIcons.Favorite className="icon-sm text-gray-300 group-hover:text-gray-600 transition-default" aria-label="Add to wishlist" />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-light-muted text-sm italic">No items recorded</p>
                    )}
                    
                    {/* Add items button (edit mode) */}
                    {isEditMode && (
                      <div>
                        <button
                          onClick={() => setShowItemPicker(!showItemPicker)}
                          className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-default text-sm"
                        >
                          <Plus className="icon-sm" />
                          Add Items from Collection
                        </button>
                        
                        {/* Item picker */}
                        {showItemPicker && (
                          <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                            {/* Search */}
                            <div className="p-2 border-b border border-gray-300 border border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                  type="text"
                                  value={itemSearchQuery}
                                  onChange={(e) => setItemSearchQuery(e.target.value)}
                                  placeholder="Search your collection..."
                                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                                />
                              </div>
                            </div>
                            
                            {/* Items list */}
                            <div className="max-h-48 overflow-y-auto">
                              {loadingItems ? (
                                <div className="p-4 text-center text-gray-500">
                                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                                  Loading...
                                </div>
                              ) : filteredCollectionItems.length > 0 ? (
                                <div className="p-2 space-y-1">
                                  {filteredCollectionItems.slice(0, 20).map((item) => (
                                    <button
                                      key={item.id}
                                      onClick={() => addItemUsed(item)}
                                      className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-default text-left"
                                    >
                                      {getCollectionItemImage(item) ? (
                                        <img
                                          src={getCollectionItemImage(item)!}
                                          alt={item.name}
                                          className="w-8 h-8 rounded object-cover"
                                        />
                                      ) : (
                                        <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-xs">
                                          💅
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-light-primary truncate">{item.name}</p>
                                        <p className="text-xs text-light-muted truncate">{item.brand}</p>
                                      </div>
                                      <Plus className="icon-sm text-gray-900" />
                                    </button>
                                  ))}
                                  {filteredCollectionItems.length > 20 && (
                                    <p className="text-xs text-gray-500 text-center py-2">
                                      Showing 20 of {filteredCollectionItems.length} items. Use search to find more.
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="p-4 text-center text-gray-500 text-sm">
                                  {itemSearchQuery ? 'No matching items' : 'No items available'}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Marketplace Section - Owner only */}
              {!isPublicView && (
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('marketplace')}
                  className="w-full px-4 py-3 border border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-default"
                >
                  <div className="flex items-center gap-2">
                    <Store className={`w-5 h-5 ${localListedOnMarketplace ? 'text-green-500' : 'text-gray-500'}`} />
                    <span className="font-bold text-gray-600 font-body">
                      Marketplace
                    </span>
                    {localListedOnMarketplace && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        localMarketplaceStatus === 'available' ? 'bg-green-100 text-green-700' :
                        localMarketplaceStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'border-2 border-gray-300 text-gray-600'
                      }`}>
                        {localMarketplaceStatus === 'available' ? 'Available' :
                         localMarketplaceStatus === 'pending' ? 'Pending' : 'Sold'}
                      </span>
                    )}
                  </div>
                  {expandedSections.has('marketplace') ? (
                    <ChevronDown className="icon-md text-gray-500" />
                  ) : (
                    <ChevronRight className="icon-md text-gray-500" />
                  )}
                </button>
                {expandedSections.has('marketplace') && (
                  <div className="p-4 space-y-4">
                    {/* Toggle Listing */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-light-secondary">List on Marketplace</span>
                      <button
                        onClick={() => {
                          const newValue = !localListedOnMarketplace;
                          setLocalListedOnMarketplace(newValue);
                          if (newValue) {
                            setLocalMarketplaceStatus('available');
                          }
                        }}
                        className={`relative w-12 h-6 rounded-full transition-default ${
                          localListedOnMarketplace ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          localListedOnMarketplace ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    {localListedOnMarketplace && (
                      <>
                        {/* Category */}
                        {isEditMode && (
                          <div>
                            <label className="block text-sm text-light-secondary mb-2">Category</label>
                            <select
                              value={localMarketplaceCategory}
                              onChange={(e) => setLocalMarketplaceCategory(e.target.value as any)}
                              className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-lg"
                            >
                              <option value="pressons">💅 Press-On Set</option>
                              <option value="jewelry">💍 Jewelry</option>
                              <option value="other">✨ Other</option>
                            </select>
                          </div>
                        )}

                        {/* Status */}
                        <div>
                          <label className="block text-sm text-light-secondary mb-2">Status</label>
                          <div className="flex gap-2">
                            {(['available', 'pending', 'sold'] as const).map((status) => (
                              <button
                                key={status}
                                onClick={() => setLocalMarketplaceStatus(status)}
                                className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-default ${
                                  localMarketplaceStatus === status
                                    ? status === 'available' ? 'bg-green-500 text-white' :
                                      status === 'pending' ? 'bg-yellow-500 text-white' :
                                      'bg-gray-500 text-white'
                                    : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Contact for Pricing */}
                        {isEditMode && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={localContactForPricing}
                              onChange={(e) => setLocalContactForPricing(e.target.checked)}
                              className="w-4 h-4 text-light-primary rounded"
                            />
                            <span className="text-sm text-light-secondary">Contact for pricing</span>
                          </label>
                        )}

                        {/* Price Display (view mode) */}
                        {!isEditMode && localPrice > 0 && (
                          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <span className="text-green-700">Listed Price</span>
                            <span className="text-xl font-bold text-green-600">${localPrice.toFixed(2)}</span>
                          </div>
                        )}
                        {!isEditMode && localContactForPricing && (
                          <div className="p-3 bg-blue-50 rounded-lg text-center">
                            <span className="text-blue-700">Contact for pricing</span>
                          </div>
                        )}

                        {/* Press-On Details */}
                        {localMarketplaceCategory === 'pressons' && isEditMode && (
                          <div className="p-3 bg-purple-50 rounded-lg space-y-3">
                            <h4 className="font-medium text-purple-700">Press-On Options</h4>
                            
                            {/* Shapes */}
                            <div>
                              <label className="text-sm text-light-secondary mb-1 block">Shapes Available</label>
                              <div className="flex flex-wrap gap-1">
                                {SHAPE_OPTIONS.map(shape => (
                                  <button
                                    key={shape}
                                    onClick={() => {
                                      if (localShapesAvailable.includes(shape)) {
                                        setLocalShapesAvailable(prev => prev.filter(s => s !== shape));
                                      } else {
                                        setLocalShapesAvailable(prev => [...prev, shape]);
                                      }
                                    }}
                                    className={`px-2 py-1 rounded-full text-xs transition-default ${
                                      localShapesAvailable.includes(shape)
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                    }`}
                                  >
                                    {shape}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Lengths */}
                            <div>
                              <label className="text-sm text-light-secondary mb-1 block">Lengths Available</label>
                              <div className="flex flex-wrap gap-1">
                                {LENGTH_OPTIONS.map(length => (
                                  <button
                                    key={length}
                                    onClick={() => {
                                      if (localLengthsAvailable.includes(length)) {
                                        setLocalLengthsAvailable(prev => prev.filter(l => l !== length));
                                      } else {
                                        setLocalLengthsAvailable(prev => [...prev, length]);
                                      }
                                    }}
                                    className={`px-2 py-1 rounded-full text-xs transition-default ${
                                      localLengthsAvailable.includes(length)
                                        ? 'bg-purple-500 text-white'
                                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                    }`}
                                  >
                                    {length}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Sizing options */}
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={localCustomSizing}
                                  onChange={(e) => setLocalCustomSizing(e.target.checked)}
                                  className="w-4 h-4 text-purple-500 rounded"
                                />
                                <span className="text-sm text-light-secondary">Custom sizing available</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={localSizingKitAvailable}
                                  onChange={(e) => setLocalSizingKitAvailable(e.target.checked)}
                                  className="w-4 h-4 text-purple-500 rounded"
                                />
                                <span className="text-sm text-light-secondary">Sizing kit available</span>
                              </label>
                            </div>

                            {/* Sizing Chart Link */}
                            <div>
                              <label className="text-sm text-light-secondary mb-1 block">Sizing Chart Link</label>
                              <input
                                type="url"
                                value={localGuideUrl}
                                onChange={(e) => setLocalGuideUrl(e.target.value)}
                                placeholder="https://example.com/sizing-chart"
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-body"
                              />
                              <p className="text-xs text-gray-500 mt-1">Link to your sizing guide or chart</p>
                            </div>
                          </div>
                        )}

                        {/* Press-On Details (view mode) */}
                        {localMarketplaceCategory === 'pressons' && !isEditMode && (localShapesAvailable.length > 0 || localLengthsAvailable.length > 0 || localCustomSizing || localSizingKitAvailable || localGuideUrl) && (
                          <div className="p-3 bg-purple-50 rounded-lg space-y-2">
                            <h4 className="font-medium text-purple-700">Press-On Options</h4>
                            {localShapesAvailable.length > 0 && (
                              <div className="text-sm">
                                <span className="text-gray-600">Shapes: </span>
                                <span className="text-purple-700">{localShapesAvailable.join(', ')}</span>
                              </div>
                            )}
                            {localLengthsAvailable.length > 0 && (
                              <div className="text-sm">
                                <span className="text-gray-600">Lengths: </span>
                                <span className="text-purple-700">{localLengthsAvailable.join(', ')}</span>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-2 text-xs">
                              {localCustomSizing && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Custom Sizing</span>
                              )}
                              {localSizingKitAvailable && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">Sizing Kit Available</span>
                              )}
                            </div>
                            {localGuideUrl && (
                              <a
                                href={localGuideUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 hover:underline font-body"
                              >
                                <ExternalLink className="icon-xs" />
                                View Sizing Chart
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Save Button (only shows when there are changes or in edit mode) - Owner only */}
        {!isPublicView && (hasChanges || isEditMode) && (
          <div className="sticky bottom-0 bg-white border-t border border-gray-300 px-4 md:px-6 py-3 flex items-center justify-between">
            <p className="text-sm text-amber-600 flex items-center gap-2">
              {hasChanges ? (
                <>
                  <AppIcons.Warning className="w-4 h-4 text-current" aria-label="Warning" />
                  You have unsaved changes
                </>
              ) : (
                <>
                  <AppIcons.EditAction className="w-4 h-4 text-current" aria-label="Edit" />
                  Edit mode
                </>
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDiscardChanges}
                className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-default font-body"
              >
                Discard
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={isSaving || !hasChanges}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-default disabled:opacity-50 font-body"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="icon-sm" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation - Owner only */}
        {!isPublicView && showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="glass-modal max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-600 mb-2 flex items-center gap-2 font-heading">
                <AppIcons.Warning className="w-6 h-6 text-current" aria-label="Warning" /> Delete Creation?
              </h3>

              <p className="text-white mb-4">
                This will permanently delete:
              </p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="font-bold text-gray-900">{creation.title}</div>
              </div>

              <p className="text-red-600 font-medium text-sm mb-6 flex items-center gap-2">
                <AppIcons.Warning className="w-4 h-4 text-current" aria-label="Warning" />
                This action cannot be undone!
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border-2 border-slate-500 text-white rounded-lg hover:bg-slate-600 transition-default font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onDelete?.(creation.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
                >
                  <AppIcons.Delete className="w-4 h-4 text-current" aria-label="Delete" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Login Prompt Modal */}
        {loginPrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 text-center">
              <div className="text-5xl mb-4">🔐</div>
              <h3 className="text-xl font-bold text-light-primary mb-2 font-heading">
                Sign In Required
              </h3>
              <p className="text-light-secondary mb-4 font-body">
                You need to be signed in to save items to your watchlist or wishlist.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setLoginPrompt(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-light-secondary rounded-lg hover:bg-gray-200 transition-default font-body"
                >
                  Cancel
                </button>
                <a
                  href="/auth"
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-default text-center font-body"
                >
                  Sign In
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Item Detail / Add to PublicWishlist Modal */}
        {selectedItemUsed && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-fuchsia-500 to-purple-500 px-4 py-3 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white font-heading">
                  Item Details
                </h3>
                <button
                  onClick={() => {
                    setSelectedItemUsed(null);
                    setWishlistSuccess(false);
                  }}
                  className="p-1 hover:bg-white/20 rounded-full transition-default"
                >
                  <X className="icon-md text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {wishlistSuccess ? (
                  <div className="text-center py-4">
                    <div className="text-5xl mb-3">🎉</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2 font-heading">
                      Added to Wishlist!
                    </h4>
                    <p className="text-gray-600 font-body">
                      You can find it in your wishlist now.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Item Preview */}
                    <div className="flex items-start gap-4 mb-6">
                      {selectedItemUsed.photoUrl ? (
                        <img
                          src={selectedItemUsed.photoUrl}
                          alt={selectedItemUsed.name}
                          className="w-24 h-24 rounded-lg object-cover shadow-md"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gradient-to-br from-fuchsia-100 to-purple-100 rounded-lg flex items-center justify-center shadow-md">
                          <span className="text-4xl">💅</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-light-primary font-body">
                          {selectedItemUsed.name}
                        </h4>
                        <p className="text-light-secondary font-body">
                          {selectedItemUsed.brand}
                        </p>
                        <p className="text-sm text-light-muted mt-2 font-body">
                          Used in this creation
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <button
                        onClick={() => handleAddToWishlist(selectedItemUsed)}
                        disabled={addingToWishlist}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 transition-default font-medium disabled:opacity-50 font-body"
                      >
                        {addingToWishlist ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          <>
                            <AppIcons.Favorite className="icon-md text-current" aria-label="Favorite" />
                            Add to My Wishlist
                          </>
                        )}
                      </button>
                      <p className="text-xs text-light-muted text-center font-body">
                        This will add the item to your wishlist so you can track it
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreationDetailModal;
